import { useState, useEffect } from 'react';
import { config } from '../../../config';
import { prepareHeaders } from '../../../configuration/tokenHandling/prepareHeaders';
import { type TableViewTogglesViewType } from '@rio-cloud/rio-uikit/TableViewToggles';
import { IAnalysis } from '../models/Analysis';
import { ITrip } from '../models/Trip';
import { useSelectVehicleStore } from '../../../services/selectVehicleSlice';
import { ISafeEvents } from '../models/SafeEvents';
import { IPredictiveEvents } from '../models/PredictiveEvents';
import { IEsgEvents } from '../models/EsgEvents';
import { useSelectExtractAssetsStore } from '../../../services/selectExtractAssetsStore';
import { useDateFilterStore } from '../../../services/dateFilterStore';
import { useGetAssetsQuery } from '../../../services/assetsApi';

type UseAnalysisDataProps = {
  externalViewType: TableViewTogglesViewType;
  behaviorType: string;
};

interface AnalysisResult {
  assetId: string;
  vehicle: string;
  chassis: string;
  tripId: string;
  behaviorType: 'SAFE' | 'PREDICTIVE' | 'ESG' | string;
  startTrip: string;
  finalTrip: string;
  tripScore: number;
  tripCumulativeScore: number;
  epk: number;
  [key: string]: any;
}

export const useAnalysisData = ({
  behaviorType
}: UseAnalysisDataProps) => {
  const [analysisList, setAnalysisList] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { setSelectedVehicle, clearSelectedVehicle, selectedVehicle } = useSelectVehicleStore();
  const { selectedExtractAssets } = useSelectExtractAssetsStore();
  const { startDate, endDate } = useDateFilterStore();
  const { currentData } = useGetAssetsQuery();

  useEffect(() => {

    const getAssets = async () => {
      setIsLoading(true);
      setError(null);
      clearSelectedVehicle();
      try {
        const headers = await prepareHeaders(new Headers());
        const response = await fetch(config.backend.SCORES_SERVICE + '/v2/scores/analysis', {
          method: 'POST',
          headers,
          body: JSON.stringify({ asset_id: selectedExtractAssets[0], behavior_type: behaviorType, start_date: startDate, end_date: endDate }),
        });
        if (!response.ok) {
          setSelectedVehicle({
            name: selectedVehicle.name,
            chassis: selectedVehicle.chassis,
            itSpeed: 0,
            itBraking: 0,
            itCoasting: 0,
            itAttentiveDriving: 0,
            itRollover: 0,
            itGoodPractices: 0,
            itKickdown: 0,
            itAcceleration: 0,
            itRpm: 0,
            itOperationalInsistence: 0,
            itClutch: 0,
            itIdling: 0,
            itIdlingAc: 0
          });
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = (await response.json()) as IAnalysis;
        const trips = data.items as ITrip[];

        let sumItSpeed = 0;
        let sumItBraking = 0;
        let sumItCoasting = 0;
        let sumItAttentiveDriving = 0;
        let sumItRollover = 0;
        let sumItGoodPractices = 0;
        let sumItKickdown = 0;
        let sumItAcceleration = 0;
        let sumItRpm = 0;
        let sumItClutch = 0;
        let sumItOperationalInsistence = 0;
        let sumItIdling = 0;
        let sumItIdlingAc = 0;

        const asset = currentData?.items.find(element=>element.id == data.assetId);
        const name = asset ? asset.name : '';
        const chassis = asset ? asset.identification : '';

        setAnalysisList(trips.map((item: ITrip) => {

          const result: AnalysisResult = {
            assetId: data.assetId,
            vehicle: name,
            chassis: chassis,
            tripId: item.tripId,
            behaviorType: behaviorType,
            startTrip: item.startTrip,
            finalTrip: item.finalTrip,
            tripScore: item.tripScore,
            tripCumulativeScore: item.tripCumulativeScore ? item.tripCumulativeScore : 0 + item.tripScore ? item.tripScore : 0,
            epk: item.epk,
          }

          switch (behaviorType) {
            case 'SAFE':
              result.itSpeed = (item.events as ISafeEvents).IT_SPEED;
              result.itBraking = (item.events as ISafeEvents).IT_BRAKING;
              result.itCoasting = (item.events as ISafeEvents).IT_COASTING;
              result.itAttentiveDriving = (item.events as ISafeEvents).IT_ATTENTIVE_DRIVING;
              result.itRollover = (item.events as ISafeEvents).IT_ROLLOVER;
              result.itGoodPractices = (item.events as ISafeEvents).IT_GOOD_PRACTICES;

              sumItSpeed += (item.events as ISafeEvents).IT_SPEED;
              sumItBraking += (item.events as ISafeEvents).IT_BRAKING;
              sumItCoasting += (item.events as ISafeEvents).IT_COASTING;
              sumItAttentiveDriving += (item.events as ISafeEvents).IT_ATTENTIVE_DRIVING;
              sumItRollover += (item.events as ISafeEvents).IT_ROLLOVER;
              sumItGoodPractices += (item.events as ISafeEvents).IT_GOOD_PRACTICES;

              break;
            case 'PREDICTIVE':
              result.itKickdown = (item.events as IPredictiveEvents).IT_KICKDOWN;
              result.itAcceleration = (item.events as IPredictiveEvents).IT_ACCELERATION;
              result.itRpm = (item.events as IPredictiveEvents).IT_RPM;
              result.itClutch = (item.events as IPredictiveEvents).IT_CLUTCH;
              result.itOperationalInsistence = (item.events as IPredictiveEvents).IT_OPERATIONAL_INSISTENCE;

              sumItKickdown += (item.events as IPredictiveEvents).IT_KICKDOWN;
              sumItAcceleration += (item.events as IPredictiveEvents).IT_ACCELERATION;
              sumItRpm += (item.events as IPredictiveEvents).IT_RPM;
              sumItClutch += (item.events as IPredictiveEvents).IT_CLUTCH;
              sumItOperationalInsistence += (item.events as IPredictiveEvents).IT_OPERATIONAL_INSISTENCE;

              break;
            case 'ESG':
              result.itIdling = (item.events as IEsgEvents).IT_IDLING;
              result.itIdlingAc = (item.events as IEsgEvents).IT_IDLING_AC;

              sumItIdling += (item.events as IEsgEvents).IT_IDLING;
              sumItIdlingAc += (item.events as IEsgEvents).IT_IDLING_AC;

              break;
          }

          return result;
        }));

        setSelectedVehicle({
          name: name,
          chassis: chassis,
          itSpeed: sumItSpeed,
          itBraking: sumItBraking,
          itCoasting: sumItCoasting,
          itAttentiveDriving: sumItAttentiveDriving,
          itRollover: sumItRollover,
          itGoodPractices: sumItGoodPractices,
          itKickdown: sumItKickdown,
          itAcceleration: sumItAcceleration,
          itRpm: sumItRpm,
          itOperationalInsistence: sumItOperationalInsistence,
          itClutch: sumItClutch,
          itIdling: sumItIdling,
          itIdlingAc: sumItIdlingAc
        });

      } catch (e: any) {
        console.error('Error fetching good practices:', e);
        setError(e.message);
      } finally {
        setIsLoading(false);
      }
    };

    getAssets();
  }, [behaviorType, startDate, endDate]);

  return {
    analysisList,
    isLoading,
    error,
  };
};